import { useState, useEffect } from "react";
import { useLanguage } from "@/i18n/index";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";
import api from "@/api";
import { toast } from "sonner";
import { Building2, Users, Activity, Bell, HeartPulse } from "lucide-react";

export default function GroupAdminDashboard() {
  const { t } = useLanguage();
  const [clinics, setClinics] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/clinics").catch(() => ({ data: [] })),
      api.get("/kpi/group").catch(() => ({ data: null })),
    ])
      .then(([c, k]) => {
        setClinics(c.data || []);
        setKpi(k.data);
        if (!k.data) toast.error("Erreur KPI groupe");
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <ProfessionalLayout title="Administration Groupe">
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 animate-fade-in-up">
          <div className="dash-card" data-testid="kpi-clinics-count">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <Building2 className="w-3.5 h-3.5" /> Cliniques actives
            </div>
            <p className="text-3xl font-bold text-sky-600">{kpi?.clinics ?? clinics.length}</p>
          </div>
          <div className="dash-card" data-testid="kpi-patients-total">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <Users className="w-3.5 h-3.5" /> Patients actifs
            </div>
            <p className="text-3xl font-bold text-emerald-600">{kpi?.patients?.total ?? "—"}</p>
          </div>
          <div className="dash-card" data-testid="kpi-checkins-month">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <HeartPulse className="w-3.5 h-3.5" /> Check-ins 30j
            </div>
            <p className="text-3xl font-bold text-indigo-600">{kpi?.checkins?.lastMonth ?? "—"}</p>
          </div>
          <div className="dash-card" data-testid="kpi-alerts-month">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <Bell className="w-3.5 h-3.5" /> Alertes 30j
            </div>
            <p className="text-3xl font-bold text-rose-600">
              {kpi?.alerts?.lastMonth ?? "—"}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {kpi?.alerts?.resolutionRate != null ? `${kpi.alerts.resolutionRate}% résolues` : ""}
            </p>
          </div>
        </div>

        <div className="dash-card animate-fade-in-up stagger-1">
          <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4" /> Mes cliniques
          </h2>
          {loading ? (
            <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />)}</div>
          ) : clinics.length === 0 ? (
            <p className="text-slate-400 text-center py-8">Aucune clinique</p>
          ) : (
            <div className="space-y-2">
              {clinics.map(clinic => (
                <div key={clinic.id} data-testid={`clinic-${clinic.id}`} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-sky-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">{clinic.name}</p>
                      <p className="text-sm text-slate-500">{clinic.address}</p>
                      <p className="text-xs text-slate-400">{clinic.phone}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ProfessionalLayout>
  );
}
